import {
  CUSTOM_PAPER_MAX_MM,
  CUSTOM_PAPER_MIN_MM,
  PAPER_GROUPS,
  PAPER_METADATA,
  PAPER_MM,
  PAPER_UNITS,
  type PaperGroup,
  type PaperUnit,
  NAMED_PAPERS as SEGNAVIA_PAPERS,
} from '@segnavia/format'
import type { FAMILY } from './fonts/manifest'

export type NamedPaper = (typeof SEGNAVIA_PAPERS)[number]
export type { PaperGroup, PaperUnit }
export { CUSTOM_PAPER_MAX_MM, CUSTOM_PAPER_MIN_MM, PAPER_GROUPS, PAPER_UNITS }

/* Segnavia names several papers alike (four postcards, four posters) and
   tells them apart by size, so a label that would repeat carries its size. */
function sharesItsName(name: NamedPaper): boolean {
  const { name: label } = PAPER_METADATA[name]
  return SEGNAVIA_PAPERS.some(
    (other) => other !== name && PAPER_METADATA[other].name === label,
  )
}

/**
 * Every paper Segnavia prints, as this platform reads it. Segnavia holds the
 * list, so a paper it adds is offered here by a version bump.
 */
export const NAMED_PAPERS = Object.fromEntries(
  SEGNAVIA_PAPERS.map((name) => [
    name,
    {
      widthMm: PAPER_MM[name].width,
      heightMm: PAPER_MM[name].height,
      label: sharesItsName(name)
        ? `${PAPER_METADATA[name].name} ${PAPER_METADATA[name].size}`
        : PAPER_METADATA[name].name,
      group: PAPER_METADATA[name].group,
      size: PAPER_METADATA[name].size,
    },
  ]),
) as Record<
  NamedPaper,
  {
    widthMm: number
    heightMm: number
    label: string
    group: PaperGroup
    size: string
  }
>

/** How a person reads each orientation. */
export const ORIENTATION_LABELS = {
  portrait: 'Portrait',
  landscape: 'Landscape',
} as const

/**
 * A custom size is held in millimeters, with the unit it was typed in so it
 * reopens as typed. A size stored without one was typed in millimeters.
 */
export type HtmlPagePaper =
  | { name: NamedPaper }
  | { name: 'custom'; widthMm: number; heightMm: number; unit?: PaperUnit }
export type HtmlPageOrientation = 'portrait' | 'landscape'
export type HtmlPageAppFont = keyof typeof FAMILY
/** The longest family name a page's sheet may name as a local face. */
export const HTML_PAGE_LOCAL_FAMILY_MAX_CHARS = 200
/** The most local family names one assistant call may carry. */
export const LOCAL_FONT_FAMILY_MAX_COUNT = 500

export type HtmlPageLocalFont = {
  local: string
  fallback: HtmlPageAppFont
}
export type HtmlPageFont = HtmlPageAppFont | HtmlPageLocalFont
export type HtmlPageFonts = HtmlPageFont[]
export const NAMED_PAPER_NAMES = [...SEGNAVIA_PAPERS] as [
  NamedPaper,
  ...NamedPaper[],
]
export const NAMED_PAPER_SQL_LIST = NAMED_PAPER_NAMES.map(
  (name) => `'${name}'`,
).join(', ')
export const PAPER_UNIT_SQL_LIST = PAPER_UNITS.map((unit) => `'${unit}'`).join(
  ', ',
)
